import React, { useState, useEffect } from 'react';
import { Star, TrendingUp, Users, MessageSquare } from 'lucide-react';
import apiClient from '../../services/apiClient';
import Navbar from '../../components/Navbar';

export default function CourseFeedbackDashboard() {
  const [programStats, setProgramStats] = useState({});
  const [loading, setLoading] = useState(true);

  const programs = [
    { slug: 'ventas-consultivas-45', title: 'Ventas Consultivas Executive' }
    // Agregar más programas según se creen
  ];

  useEffect(() => {
    loadAllStats();
  }, []);

  const loadAllStats = async () => {
    setLoading(true);
    const stats = {};

    for (const program of programs) {
      try {
        const response = await apiClient.get(`/course-feedback/program/${program.slug}`);
        stats[program.slug] = {
          title: program.title,
          feedbacks: response.data.feedbacks
        };
      } catch (error) {
        console.error(`Error al cargar stats de ${program.slug}:`, error);
      }
    }

    setProgramStats(stats);
    setLoading(false);
  };

  const calculateAverage = (feedbacks) => {
    if (feedbacks.length === 0) return 0;
    const sum = feedbacks.reduce((acc, f) => acc + f.rating, 0);
    return (sum / feedbacks.length).toFixed(1);
  };

  const getTopTopics = (feedbacks) => {
    const topicCounts = {};
    feedbacks.forEach(f => {
      f.topics.forEach(topic => {
        topicCounts[topic] = (topicCounts[topic] || 0) + 1;
      });
    });

    return Object.entries(topicCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando estadísticas...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-gray-50">
        <section className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-3xl font-bold mb-3">Dashboard de Feedback</h1>
            <p className="text-gray-300">Métricas de calidad y temas de interés</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 py-12">
          {Object.entries(programStats).map(([slug, data]) => {
            const { feedbacks, title } = data;
            const avgRating = calculateAverage(feedbacks);
            const topTopics = getTopTopics(feedbacks);

            return (
              <div key={slug} className="bg-white rounded-xl shadow-sm p-8 mb-8 border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">{title}</h2>

                {/* Métricas principales */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 border-2 border-amber-200">
                    <div className="flex items-center gap-3 mb-2">
                      <Star className="w-6 h-6 text-amber-600 fill-amber-600" />
                      <span className="text-sm font-medium text-slate-600">Rating Promedio</span>
                    </div>
                    <div className="text-4xl font-bold text-slate-900">{avgRating}</div>
                    <div className="text-sm text-slate-600 mt-1">de 5.0</div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="w-6 h-6 text-blue-600" />
                      <span className="text-sm font-medium text-slate-600">Respuestas</span>
                    </div>
                    <div className="text-4xl font-bold text-slate-900">{feedbacks.length}</div>
                    <div className="text-sm text-slate-600 mt-1">ejecutivos</div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-6 border-2 border-emerald-200">
                    <div className="flex items-center gap-3 mb-2">
                      <MessageSquare className="w-6 h-6 text-emerald-600" />
                      <span className="text-sm font-medium text-slate-600">Comentarios</span>
                    </div>
                    <div className="text-4xl font-bold text-slate-900">
                      {feedbacks.filter(f => f.comment).length}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">con feedback</div>
                  </div>
                </div>

                {/* Top Temas de Interés */}
                {topTopics.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      Temas de Mayor Interés
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {topTopics.map(([topic, count]) => (
                        <div
                          key={topic}
                          className="bg-purple-100 text-purple-700 px-5 py-3 rounded-lg font-semibold border-2 border-purple-200"
                        >
                          {topic} <span className="text-purple-500">({count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comentarios recientes */}
                {feedbacks.filter(f => f.comment).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Comentarios Recientes
                    </h3>
                    <div className="space-y-4">
                      {feedbacks
                        .filter(f => f.comment)
                        .slice(-5)
                        .reverse()
                        .map((feedback, idx) => (
                          <div
                            key={idx}
                            className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= feedback.rating
                                        ? 'fill-amber-400 text-amber-400'
                                        : 'text-slate-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-xs text-slate-500">
                                {new Date(feedback.createdAt).toLocaleDateString('es-ES')}
                              </span>
                            </div>
                            <p className="text-slate-700 text-sm">{feedback.comment}</p>
                            {feedback.topics.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {feedback.topics.map((topic) => (
                                  <span
                                    key={topic}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                                  >
                                    {topic}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {feedbacks.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No hay feedback aún para este programa</p>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </main>
    </>
  );
}
